import { useCallback, useMemo } from "react"
import { useDispatch } from "react-redux"
import { chain, chunk, get, map } from "lodash"
import { Creators } from "model/apiWrapper"
import { combineTanStackStatuses, OK } from "helpers/data-statuses"
import { useAccountAlias, useIsOpPro } from "resources/account"
import useCombinedQueries from "hooks/combine-queries"
import { getCompositionType, LONG } from "resources/position-sets/composition"

const PORTFOLIO_EXPERIMENT_QUERY = `query ($portfolioId: String!, $modelId: String!, $currentDate: Date!, $segment: Segment, $experimentId: String!) {
    portfolio (id: $portfolioId) {
      experiment(id: $experimentId) {
        name
        availableFrom
      }
    }
    model (id: $modelId) {
      simulation (positionSet: {
        type: PORTFOLIO,
        id: $portfolioId,
        experimentId: $experimentId,
        segment: $segment
      }, from: $currentDate, to: $currentDate) {
        composition {
          compositionBy (groupBy: LONG_SHORT) {
            id
          }
        }
      }
    }
  }`

const getPortfolioQuery = (isOpPro) => `query ($portfolioId: String!, $modelId: String!, $currentDate: Date!, $segment: Segment) {
  portfolio (id: $portfolioId) {
    name
    availableFrom
    alias
    ${!isOpPro ? `
    intradayEnabled
    betaInstrument {
      indexId
      benchmarkId
      securityId {
        internalId
        ticker
        mic
      }
      resource {
        type
        alias
      }
    }` : ""}
    fxHedgeEnabled
  }
  model (id: $modelId) {
    simulation (positionSet: {
      type: PORTFOLIO,
      id: $portfolioId,
      segment: $segment
    }, from: $currentDate, to: $currentDate) {
      composition {
        referenceEquity
        compositionBy (groupBy: LONG_SHORT) {
          id
        }
      }
    }
  }
}`

const SECURITY_QUERY = `query ($modelId: String!, $id: String!, $currentDate: Date!, $segment: Segment) {
  model (id: $modelId) {
    security (internalId: $id) {
      availability {
        to
        from
      }
      descriptors (on: $currentDate) {
        name
        assetClass
        assetSubclass
        hasComposition
        internalId
        ticker
        mic
      }
    }
  }
  model (id: $modelId) {
    simulation (positionSet: {
      dates: { date: $currentDate, equities: { id: { internalId: $id }, economicExposure: 1 } },
      expand: true,
      segment: $segment
    }, from: $currentDate, to: $currentDate) {
      composition {
        compositionBy (groupBy: LONG_SHORT) {
          id
        }
      }
    }
  }
}`

const BASKET_QUERY = `query ($modelId: String!, $ticker: String!, $currentDate: Date!, $segment: Segment) {
  swap (ticker: $ticker) {
    description
    availableFrom
    terminationDate
  }
  model (id: $modelId) {
    simulation (positionSet: {
      type: SWAP,
      id: $ticker,
      segment: $segment
    }, from: $currentDate, to: $currentDate) {
      composition {
        compositionBy (groupBy: LONG_SHORT) {
          id
        }
      }
    }
  }
}`

// [TODO] single benchmark
const BENCHMARKS_QUERY = `query ($modelId: String!) {
  model (id: $modelId) {
    benchmarks {
      id
      name
      availableFrom
      currentDate
    }
  }
}`

const INDEX_QUERY = `query ($modelId: String!, $indexId: String!, $currentDate: Date!, $segment: Segment) {
  model (id: $modelId) {
    security (indexId: $indexId) {
      availability {
        to
        from
      }
      descriptors (on: $currentDate) {
        name
        hasComposition
        assetClass
      }
    }
    simulation (positionSet: {
      type: INDEX,
      id: $indexId,
      segment: $segment
    }, from: $currentDate, to: $currentDate) {
      composition {
        compositionBy (groupBy: LONG_SHORT) {
          id
        }
      }
    }
  }
}`

const INLINE_PS_QUERY = `query ($modelId: String!, $currentDate: Date!, $positionSetDates: [PositionSetDateInput!], $segment: Segment) {
  model (id: $modelId) {
    simulation (positionSet: {dates: $positionSetDates, segment: $segment}, from: $currentDate, to: $currentDate) {
      composition {
        compositionBy (groupBy: LONG_SHORT) {
          id
        }
      }
    }
  }
}`

function portfolioSelect(data) {
  return {
    name: get(data, "data.portfolio.name"),
    earliestDate: get(data, "data.portfolio.availableFrom"),
    unavailable: get(data, "apiMessages.errors.0.message", "").match(/^Portfolio .* not found$/),
    fxHedgeEnabled: get(data, "data.portfolio.fxHedgeEnabled"),
    compositionType: getCompositionType(data),
    betaInstrument: get(data, "data.portfolio.betaInstrument"),
    alias: get(data, "data.portfolio.alias"),
    intradayEnabled: get(data, "data.portfolio.intradayEnabled"),
    type: "PORTFOLIO",
    referenceEquity: get(data, "data.model.simulation.composition.0.referenceEquity"),
    // no latest date, all portfolios can be rolled over
  }
}

function portfolioExperimentSelect(data) {
  return {
    name: get(data, "data.portfolio.experiment.name"),
    earliestDate: get(data, "data.portfolio.experiment.availableFrom"),
    type: "EXPERIMENT",
    compositionType: getCompositionType(data),
  }
}

function basketSelect(data) {
  return {
    name: get(data, "data.swap.description"),
    earliestDate: get(data, "data.swap.availableFrom"),
    latestDate: get(data, "data.swap.terminationDate"),
    unavailable: get(data, "apiMessages.errors.0.message", "").startsWith("Invalid swap ticker:"),
    type: "SWAP",
    compositionType: getCompositionType(data),
  }
}

function etfSelect(data) {
  const assetClass = get(data, "data.model.security.descriptors.assetClass")
  const assetSubclass = get(data, "data.model.security.descriptors.assetSubclass")
  const type = assetClass === "index" ? "INDEX" : assetSubclass === "etf" ? "ETF" : "SECURITY"

  return {
    name: get(data, "data.model.security.descriptors.name"),
    earliestDate: get(data, "data.model.security.availability.from"),
    latestDate: get(data, "data.model.security.availability.to"),
    unavailable: get(data, "data.model.security.availability") === null,
    hasComposition: get(data, "data.model.security.descriptors.hasComposition"),
    internalId: get(data, "data.model.security.descriptors.internalId"),
    ticker: get(data, "data.model.security.descriptors.ticker"),
    mic: get(data, "data.model.security.descriptors.mic"),
    compositionType: getCompositionType(data),
    type,
  }
}

function indexSelect(data, indexId) {
  const assetClass = get(data, "data.model.security.descriptors.assetClass")
  const assetSubclass = get(data, "data.model.security.descriptors.assetSubclass")
  const type = assetClass === "index" ? "INDEX" : assetSubclass === "etf" ? "ETF" : "SECURITY"

  return {
    name: get(data, "data.model.security.descriptors.name"),
    earliestDate: get(data, "data.model.security.availability.from"),
    latestDate: get(data, "data.model.security.availability.to"),
    unavailable: get(data, "data.model.security.availability") === null,
    hasComposition: get(data, "data.model.security.descriptors.hasComposition"),
    ticker: indexId,
    compositionType: getCompositionType(data),
    type,
  }
}

function benchmarkSelect(data, positionSet) {
  return chain(data)
    .get("data.model.benchmarks")
    .find(b => b.id === positionSet.id)
    .thru(b => {
      if (b) {
        return { type: "BENCHMARK", name: b.name, compositionType: LONG, earliestDate: b.availableFrom, latestDate: b.currentDate, unavailable: false }
      } else {
        return { type: "BENCHMARK", unavailable: true }
      }
    })
    .value()
}

function comparePositionSetDates(positionSetDate1, positionSetDate2) {
  if (positionSetDate1.date < positionSetDate2.date) {
    return -1
  }
  if (positionSetDate1.date > positionSetDate2.date) {
    return 1
  }
  return 0
}

// TODO: This function is duplicated in resources/position-sets/inline.ts
// The Compare component should be migrated to use the new resources/position-sets architecture
// Until then, changes to this function must be kept in sync with inline.ts
function inlineSelect(data, positionSet) {
  return {
    name: positionSet.name || "Inline Position Set", // [TODO] Find a way to name those position sets
    earliestDate: positionSet.dates.toSorted(comparePositionSetDates)?.[0]?.date,
    compositionType: getCompositionType(data),
  }
}

function mapPositionSet(ps, modelId, currentDate, dispatch, accountAlias, isOpPro) {
  if (!ps) {
    return null
  }

  let query, variables, queryKey, select
  switch (ps.type) {
    case "PORTFOLIO":
      if (ps.experimentId) {
        query = PORTFOLIO_EXPERIMENT_QUERY
        variables = { portfolioId: ps.id, experimentId: ps.experimentId, modelId, currentDate, segment: ps.segment }
        queryKey = ["position-set", "metadata", "portfolio-experiment", variables]
        select = portfolioExperimentSelect
      } else {
        query = getPortfolioQuery(isOpPro)
        variables = { portfolioId: ps.id, modelId, currentDate, segment: ps.segment }
        queryKey = ["position-set", "metadata", "portfolio", variables]
        select = portfolioSelect
      }
      break
    case "SWAP":
    case "BASKET":
      query = BASKET_QUERY
      variables = { ticker: ps.id, modelId, currentDate, segment: ps.segment }
      queryKey = ["position-set", "metadata", "basket", variables]
      select = basketSelect
      break
    case "ETF":
    case "SECURITY":
      query = SECURITY_QUERY
      variables = { modelId, currentDate, id: ps.id, segment: ps.segment }
      queryKey = ["position-set", "metadata", "security", variables]
      select = etfSelect
      break
    case "INDEX":
      query = INDEX_QUERY
      variables = { modelId, currentDate, indexId: ps.id, segment: ps.segment }
      queryKey = ["position-set", "metadata", "index", variables]
      select = data => indexSelect(data, ps.id)
      break
    case "BENCHMARK":
      query = BENCHMARKS_QUERY
      variables = { modelId }
      // [HACK]: We need the ID here even if the query is the same for all benchmarks,
      // as the select is different.
      queryKey = ["position-set", "metadata", "benchmarks", ps.id]
      select = data => benchmarkSelect(data, ps)
      break
    case "RULES": {
      const result = mapPositionSet(ps.rulesConfiguration.source.positionSet, modelId, currentDate, dispatch, accountAlias, isOpPro)
      if (!result) {
        return null
      }

      return {
        ...result,
        select: data => ({
          ...result.select(data),
          name: "Equal Weight",
          type: ps.type,
        }),
      }
    }
    case undefined:
      query = INLINE_PS_QUERY
      variables = { modelId, currentDate, segment: ps.segment, positionSetDates: ps.dates }
      queryKey = ["position-set", "metadata", "inline", variables]
      select = data => inlineSelect(data, ps)
      break
  }

  return {
    queryKey,
    queryFn: () => new Promise((resolve, reject) => {
      dispatch(Creators.graphql(accountAlias, query, variables, resolve, reject))
    }),
    select,
  }
}

/**
 * @deprecated Components using this hook should migrate
 * to the same input/outputs offered by resources/position-sets
 */
export function usePositionSetsMetadata(modelId, positionSets, contextEarliestDate, contextLatestDate) {
  const dispatch = useDispatch()
  const accountAlias = useAccountAlias()
  const isOpPro = useIsOpPro()

  const queries = useMemo(
    () => ([
      ...map(positionSets, (ps) => mapPositionSet(ps, modelId, contextLatestDate, dispatch, accountAlias, isOpPro)),
      ...map(positionSets, (ps) => mapPositionSet(ps.base, modelId, contextLatestDate, dispatch, accountAlias, isOpPro)),
    ]),
    [dispatch, accountAlias, modelId, positionSets, contextLatestDate, isOpPro],
  )

  const combine = useCallback((queryResults) => {
    const status = combineTanStackStatuses(queryResults)
    if (status !== OK) {
      return [status, {}]
    }

    const metadata = map(queryResults, "data")

    const earliestDate = chain(metadata)
      .map("earliestDate")
      .concat(contextEarliestDate)
      .filter()
      .max()
      .value()

    const latestDate = chain(metadata)
      .map("latestDate")
      .concat(contextLatestDate)
      .filter()
      .min()
      .value()

    const [positionSetsMetadata, basesMetadata] = chunk(metadata, metadata.length / 2)

    return [status, { positionSetsMetadata, basesMetadata, earliestDate, latestDate }]
  }, [contextEarliestDate, contextLatestDate])

  return useCombinedQueries(
    queries,
    combine,
  )
}
